import { path } from 'ramda'

import { Project } from '@common/config'
import { isAbortError } from '@common/utils'

const REDLINE_REPORT_DATA_LOADING = 'REDLINE_REPORT_DATA_LOADING'
const REDLINE_REPORT_DATA_LOADED = 'REDLINE_REPORT_DATA_LOADED'
const REDLINE_REPORT_DATA_FAILED = 'REDLINE_REPORT_DATA_FAILED'

const NOISE_REPORT_DATA_LOADING = 'NOISE_REPORT_DATA_LOADING'
const NOISE_REPORT_DATA_LOADED = 'NOISE_REPORT_DATA_LOADED'
const NOISE_REPORT_DATA_FAILED = 'NOISE_REPORT_DATA_FAILED'

const DATA_QUALITY_REPORT_DATA_LOADING = 'DATA_QUALITY_REPORT_DATA_LOADING'
const DATA_QUALITY_REPORT_DATA_LOADED = 'DATA_QUALITY_REPORT_DATA_LOADED'
const DATA_QUALITY_REPORT_DATA_FAILED = 'DATA_QUALITY_REPORT_DATA_FAILED'

const REPORT_DATA_RESET_STATE = 'REPORT_DATA_RESET_STATE'

const defaultState = {}

const entityName = 'reportData'

export default {
  name: entityName,
  reducer: (state, action) => {
    if (action.type === REDLINE_REPORT_DATA_LOADING) {
      return { ...state, redline: { ...action.meta, payload: action.payload } }
    }
    if (action.type === REDLINE_REPORT_DATA_LOADED) {
      return { ...state, redline: { ...action.meta, data: action.payload } }
    }
    if (action.type === REDLINE_REPORT_DATA_FAILED) {
      return { ...state, redline: { ...action.meta, error: action.payload } }
    }
    if (action.type === NOISE_REPORT_DATA_LOADING) {
      return { ...state, noise: { ...action.meta, payload: action.payload } }
    }
    if (action.type === NOISE_REPORT_DATA_LOADED) {
      return { ...state, noise: { ...action.meta, data: action.payload } }
    }
    if (action.type === NOISE_REPORT_DATA_FAILED) {
      return { ...state, noise: { ...action.meta, error: action.payload } }
    }
    if (action.type === DATA_QUALITY_REPORT_DATA_LOADING) {
      return { ...state, dataQuality: { ...action.meta, payload: action.payload } }
    }
    if (action.type === DATA_QUALITY_REPORT_DATA_LOADED) {
      return { ...state, dataQuality: { ...action.meta, data: action.payload } }
    }
    if (action.type === DATA_QUALITY_REPORT_DATA_FAILED) {
      return { ...state, dataQuality: { ...action.meta, error: action.payload } }
    }
    return state || defaultState
  },
  selectRedlineReportDataIsLoading: ({ reportData }) => {
    const status = path(['redline', 'status'], reportData)
    return status === 'loading'
  },
  selectNoiseReportDataIsLoading: ({ reportData }) => {
    const status = path(['noise', 'status'], reportData)
    return status === 'loading'
  },
  selectDataQualityReportDataIsLoading: ({ reportData }) => {
    const status = path(['dataQuality', 'status'], reportData)
    return status === 'loading'
  },
  selectRedlineReportData: ({ reportData }) => path(['redline', 'data'], reportData),
  selectNoiseReportData: ({ reportData }) => path(['noise', 'data'], reportData),
  selectDataQualityReportData: ({ reportData }) =>
    path(['dataQuality', 'data'], reportData),
  selectRedlineReportDataError: ({ reportData }) =>
    path(['redline', 'error'], reportData),
  selectNoiseReportDataError: ({ reportData }) => path(['noise', 'error'], reportData),
  selectDataQualityReportDataError: ({ reportData }) =>
    path(['dataQuality', 'error'], reportData),
  doResetReportDataState:
    () =>
    ({ dispatch }) =>
      dispatch({ type: REPORT_DATA_RESET_STATE }),
  doFetchRedlineReportData:
    (eventId) =>
    async ({ dispatch, apiFetch }) => {
      try {
        dispatch({
          type: REDLINE_REPORT_DATA_LOADING,
          payload: null,
          meta: { status: 'loading' },
        })

        const response = await apiFetch(`/events/${eventId}/red_line_report/`, null, {
          cancelationPrefix: `${entityName}_redline`,
        })

        dispatch({
          type: REDLINE_REPORT_DATA_LOADED,
          payload: response,
          meta: { status: 'succeeded' },
        })
        return response
      } catch (err) {
        if (!isAbortError(err)) {
          dispatch({
            type: REDLINE_REPORT_DATA_FAILED,
            payload: err,
            meta: { status: 'failed' },
          })
          throw err
        }
        return null
      }
    },
  doFetchNoiseReportData:
    (eventId) =>
    async ({ dispatch, apiFetch }) => {
      try {
        dispatch({
          type: NOISE_REPORT_DATA_LOADING,
          payload: null,
          meta: { status: 'loading' },
        })

        const endpoint = Project.isRest
          ? `/events/${eventId}/summary/`
          : `/events/${eventId}/noise_report/`
        const response = await apiFetch(
          endpoint,
          {
            dataTypes: ['nrs'],
            timeline: 'EVENT',
          },
          {
            cancelationPrefix: `${entityName}_noise`,
          },
        )

        dispatch({
          type: NOISE_REPORT_DATA_LOADED,
          payload: response,
          meta: { status: 'succeeded' },
        })
        return response
      } catch (err) {
        if (!isAbortError(err)) {
          dispatch({
            type: NOISE_REPORT_DATA_FAILED,
            payload: err,
            meta: { status: 'failed' },
          })
          throw err
        }
        return null
      }
    },
  doFetchDataQualityReportData:
    (eventId) =>
    async ({ dispatch, apiFetch }) => {
      try {
        dispatch({
          type: DATA_QUALITY_REPORT_DATA_LOADING,
          payload: null,
          meta: { status: 'loading' },
        })

        const response = await apiFetch(
          `/events/${eventId}/data_quality_report/`,
          null,
          { cancelationPrefix: `${entityName}_data_quality` },
        )

        dispatch({
          type: DATA_QUALITY_REPORT_DATA_LOADED,
          payload: response,
          meta: { status: 'succeeded' },
        })
        return response
      } catch (err) {
        if (!isAbortError(err)) {
          dispatch({
            type: DATA_QUALITY_REPORT_DATA_FAILED,
            payload: err,
            meta: { status: 'failed' },
          })
          throw err
        }
        return null
      }
    },
}
